import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminPanel.css";
import AdminSidebar from "./AdminSidebar";
import AdministratorSidebar from "./AdministratorSidebar";
import UserSidebar from "./UserSidebar";

const API_BASE_URL = "https://api.saadmotors.com/sales_api.php";

// Utility function to format date between DD-MM-YYYY and YYYY-MM-DD
// const formatDate = (dateStr, toDDMMYYYY = true) => {
//   if (!dateStr) return '';
//   try {
//     const date = new Date(dateStr);
//     if (isNaN(date.getTime())) return '';

//     if (toDDMMYYYY) {
//       const day = String(date.getDate()).padStart(2, '0');
//       const month = String(date.getMonth() + 1).padStart(2, '0');
//       const year = date.getFullYear();
//       return `${day}-${month}-${year}`;
//     } else {
//       const year = date.getFullYear();
//       const month = String(date.getMonth() + 1).padStart(2, '0');
//       const day = String(date.getDate()).padStart(2, '0');
//       return `${year}-${month}-${day}`;
//     }
//   } catch (error) {
//     console.error("Error formatting date:", error);
//     return '';
//   }
// };

const formatDate = (dateStr) => {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');

    const month = date.toLocaleString('en-US', {
      month: 'short',
    }).toUpperCase();

    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return '';
  }
};

// Validate DD-MM-YYYY format
const isValidDDMMYYYY = (dateStr) => {
  const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
  if (!regex.test(dateStr)) return false;

  const [day, month, year] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getDate() === day &&
    date.getMonth() + 1 === month &&
    date.getFullYear() === year
  );
};

// Determine sale status based on etd and eta
const getSaleStatus = (etd, eta) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0); // Normalize to start of day

  if (!etd || etd === '' || etd === null) {
    return "Under Process";
  }

  const etdDate = new Date(etd);
  const etaDate = eta ? new Date(eta) : null;

  if (etaDate && !isNaN(etaDate.getTime()) && etaDate < currentDate) {
    return "Arrived";
  }

  if (etdDate && !isNaN(etdDate.getTime()) && etdDate < currentDate) {
    return "Shipped";
  }

  return "Under Process";
};

const ViewSales = () => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState("");
  const [userFullName, setUserFullName] = useState("");
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [userName, setUserName] = useState("");
  const [carStockOptions, setCarStockOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [dateError, setDateError] = useState("");
  const [priceError, setPriceError] = useState(""); // For price validation

  useEffect(() => {
    const storedUserType = localStorage.getItem("usertype");
    const storedUserName = localStorage.getItem("userName");
    const storedFName = localStorage.getItem("fname");
    const storedLName = localStorage.getItem("lname");

    if (!storedUserType) {
      navigate("/login");
    } else {
      setUserType(storedUserType);
      setUserName(storedUserName);
      if (storedFName && storedLName) {
        setUserFullName(`${storedFName} ${storedLName}`);
      } else if (storedFName) {
        setUserFullName(storedFName);
      } else {
        setUserFullName(storedUserName);
      }
    }

    fetchSales();
    fetchCarStockOptions();
    fetchClientOptions();
  }, [navigate]);

  const fetchClientOptions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=get_users`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch client options');
      }

      setClientOptions(data.data || []);
    } catch (error) {
      console.error("Error fetching client options:", error);
      setError(error.message);
    }
  };

  const fetchCarStockOptions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=get_car_details`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch car details');
      }

      setCarStockOptions(data.data || []);
    } catch (error) {
      console.error("Error fetching car details:", error);
      setError(error.message);
    }
  };

  const fetchSales = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}?action=get_sales`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch sales');
      }

      setSales(data.data || []);
    } catch (error) {
      console.error("Error fetching sales:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaleForEdit = async (saleId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}?action=get_sale&id=${saleId}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch sale details');
      }

      // Format sale_date to YYYY-MM-DD for date input
      const saleData = {
        ...data.data,
        sale_date: formatDate(data.data.sale_date, false),
        price: data.data.sold_price !== null ? data.data.sold_price : data.data.car_price
      };

      setEditingSale(saleId);
      setEditFormData(saleData);
      setDateError("");
      setPriceError("");
    } catch (error) {
      console.error("Error fetching sale:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "client_id") {
      const selectedClient = clientOptions.find(client => client.id === parseInt(value));
      setEditFormData(prev => ({
        ...prev,
        client_id: value,
        client_name: selectedClient ? `${selectedClient.fname} ${selectedClient.lname}` : prev.client_name
      }));
    } else if (name === "sale_date") {
      // Validate date input (YYYY-MM-DD format from date picker)
      if (value === "" || !isNaN(new Date(value).getTime())) {
        setEditFormData(prev => ({
          ...prev,
          sale_date: value
        }));
        setDateError("");
      } else {
        setDateError("Invalid date selected");
      }
    } else if (name === "price") {
      setEditFormData(prev => ({
        ...prev,
        price: value
      }));
      // Validate price
      const parsedValue = parseFloat(value);
      if (value === "") {
        setPriceError("");
      } else if (isNaN(parsedValue) || parsedValue < 0) {
        setPriceError("Price must be a positive number");
      } else {
        setPriceError("");
      }
    } else {
      setEditFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const saveEditedSale = async () => {
    try {
      if (dateError) {
        throw new Error("Please fix the date format before saving");
      }
      if (priceError) {
        throw new Error("Please fix the price before saving");
      }

      if (!editFormData.sale_date) {
        throw new Error("Sale date is required");
      }

      setLoading(true);

      // sale_date is already in YYYY-MM-DD format from date input
      const apiSaleDate = editFormData.sale_date;

      // Update sold_price in car_details if price was edited
      const originalPrice = editFormData.sold_price !== null ? editFormData.sold_price : editFormData.car_price;
      if (editFormData.price && parseFloat(editFormData.price) !== parseFloat(originalPrice)) {
        const priceUpdateResponse = await fetch(`${API_BASE_URL}?action=update_sold_price`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            stock_id: editFormData.stock_id,
            sold_price: parseFloat(editFormData.price)
          })
        });

        const priceUpdateData = await priceUpdateResponse.json();
        if (!priceUpdateResponse.ok || !priceUpdateData.success) {
          throw new Error(priceUpdateData.message || 'Failed to update price');
        }
      }

      // Prepare data to update the sale
      const updateData = {
        id: editFormData.id,
        client_id: editFormData.client_id,
        client_name: editFormData.client_name,
        stock_id: editFormData.stock_id,
        sale_date: apiSaleDate
      };

      const response = await fetch(`${API_BASE_URL}?action=update_sale`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to update sale');
      }

      // Refresh the sales list to reflect updated price
      await fetchSales();
      setEditingSale(null);
      setDateError("");
      setPriceError("");
    } catch (error) {
      console.error("Error updating sale:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("userName");
    localStorage.removeItem("usertype");
    localStorage.removeItem("fname");
    localStorage.removeItem("lname");
    navigate("/login");
  };

  return (
    <div className="admin-container">
      <div className="sidebar-container">
        {userType === "administrator" && <AdministratorSidebar />}
        {userType === "admin" && <AdminSidebar />}
        {userType === "user" && <UserSidebar />}
      </div>

      <div className="admin-content">
        <div className="adminnavbar">
          <h1>Welcome to the Admin Panel, {userFullName}</h1>
          <button onClick={logout} className="logoutbtn">
            Logout
          </button>
        </div>

        <div className="mainsection">
          <h3>View All Sales</h3>

          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          {loading && !editingSale && (
            <div className="text-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {!loading && !editingSale && (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="">
                  <tr>
                    <th style={{textAlign:"center"}}>Date</th>
                    <th style={{textAlign:"center"}}>Client Name</th>
                    <th style={{textAlign:"center"}}>Stock ID</th>
                    <th style={{textAlign:"center"}}>Maker Details</th>
                    <th style={{textAlign:"center"}}>Chassis #</th>
                    <th style={{textAlign:"center"}}>CNF Price</th>
                    <th style={{textAlign:"center"}}>Status</th>
                    <th style={{textAlign:"center"}}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.length > 0 ? (
                    sales.map((sale) => (
                      <tr key={sale.id}>
                        <td style={{textAlign:"center"}}>{formatDate(sale.sale_date)}</td>
                        <td style={{textAlign:"center"}}>{sale.client_name}</td>
                        <td style={{textAlign:"center"}}>{sale.stock_id}</td>
                        <td style={{textAlign:"center"}}>{sale.maker} {sale.model} {sale.year}</td>
                        <td style={{textAlign:"center"}}>{sale.vin}</td>
                        <td style={{ textAlign: 'right', paddingRight: '1%' }}>
                          ${sale.sold_price !== null ? sale.sold_price : sale.price}
                        </td>
                        <td style={{ textAlign: 'center' }}>{getSaleStatus(sale.etd, sale.eta)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => fetchSaleForEdit(sale.id)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">No sales found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {editingSale && (
            <div className="card mt-4">
              <div className="card-header">
                <h4>Edit Sale</h4>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center my-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <form>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Client Name</label>
                        <select
                          className="form-control"
                          name="client_id"
                          value={editFormData.client_id || ''}
                          onChange={handleEditInputChange}
                          required
                        >
                          <option value="">Select Client</option>
                          {clientOptions.map((client) => (
                            <option key={client.id} value={client.id}>
                              {client.fname} {client.lname}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Stock ID</label>
                        <select
                          className="form-control"
                          name="stock_id"
                          value={editFormData.stock_id || ''}
                          onChange={handleEditInputChange}
                        >
                          <option value="">Select Stock ID</option>
                          {carStockOptions.map((car) => (
                            <option key={car.stock_id} value={car.stock_id}>
                              {car.stock_id} - {car.maker} {car.model}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-4">
                        <label className="form-label">Maker</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editFormData.maker || ''}
                          readOnly
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Model</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editFormData.model || ''}
                          readOnly
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">CNF Price</label>
                        <input
                          type="text"
                          className="form-control"
                          name="price"
                          value={editFormData.price || ''}
                          onChange={handleEditInputChange}
                          placeholder="Enter price"
                        />
                        {priceError && (
                          <div className="text-danger mt-1">{priceError}</div>
                        )}
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Sale Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="sale_date"
                          value={editFormData.sale_date || ''}
                          onChange={handleEditInputChange}
                          required
                        />
                        {dateError && (
                          <div className="text-danger mt-1">{dateError}</div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => {
                          setEditingSale(null);
                          setPriceError("");
                          setDateError("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={saveEditedSale}
                        disabled={loading || dateError || priceError}
                      >
                        {loading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewSales;